// src/App.js
import React, { Component } from "react";
import axios from "axios";
import Header from "./components/HeaderNav";
import Sidebar from "./components/Sidebar";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Home from "./components/Home";
import Clientes from "./pages/clientes";
import ImportarClientes from "./pages/importar_clientes";
import Partners from "./pages/partners";
import Proyectos from "./pages/proyectos";
import Productos from "./pages/productos";
import ClasificacionProductos from "./pages/clasificacion_productos";
import Costo from "./pages/costos";
import Sede from "./pages/sede";
import DetalleProyectos from "./pages/detalle_proyecto";
import Movimiento from "./pages/movimientos";
import Authorized from "./components/Auth";
import MultiCom from "./pages/multiCom";
import EstadoInstructor from "./pages/estado_instructor";
import Utilidades from "./pages/utilidad";
import WeekChart from "./pages/week";
import BoxScore from "./pages/BoxScore";
import BoxScoreHistorical from "./pages/box_score_historical";
import SpcComponent from "./pages/spc";
import Pcc from "./pages/pcc";
import Chart from 'chart.js/auto';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';
import Project from "./Projects/Project";
import Lideres from "./pages/lideres_proyecto";
import Vendors from "./pages/vendors";
import InvoiceForm from "./pages/InvoiceForm";
import Analytics from "./pages/analytics";
import Proyecto_batch from "./pages/proyecto_batch";
import Subproyecto_batch from "./pages/proyecto_subbatch";
import RequireAuth from "./components/RequireAuth";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmInstructorResult from "./components/ProyectoDetail/Instructor/ConfirmInstructorResult";
import ConfirmUtilidadResult from "./components/Utilidades/ConfirmUtilidadResult";
import Reportes from "./pages/reportes";
import Eula from "./pages/legal/eula";
import Privacy from "./pages/legal/privacy";
import Dashboards from "./pages/dashboards";
import BusinessIntelligence from "./pages/business-intelligence";
import BiOnline from "./pages/bi-online";
import SecuritySettings from "./pages/security-settings";
import UserAdmin from "./pages/user-admin";
import ForgotPassword from "./pages/forgot-password";
import ResetPassword from "./pages/reset-password";
import AI from "./pages/ai";
import AIAdmin from "./pages/ai-admin";
import Estado from "./pages/estado";
import { refreshTokens } from "./utils/auth";


// Every axios call sends the HttpOnly access/refresh cookies. This is what makes
// the ~95 files calling axios.get(url, { headers: getAuthHeaders() }) keep working
// after we drop the Authorization header from getAuthHeaders().
axios.defaults.withCredentials = true;

const AUTH_USER_KEY = "lssi-auth-user";
const REFRESH_INTERVAL_MS = 45 * 60 * 1000; // 45 min (access token is 1 hour)
const API_BASE = "https://api.lssi-fin.com";

// On 401 for a normal request, try refreshing the token once and replay the
// original request. Only fall back to logout-and-redirect if the refresh itself
// fails (truly expired session). Skip /api/auth/ to avoid loops, and tag the
// retried config so a second 401 doesn't recurse.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    const url = config?.url || "";
    const isAuthEndpoint = url.includes("/api/auth/");
    const hasUser = !!localStorage.getItem(AUTH_USER_KEY);

    if (status === 401 && !isAuthEndpoint && hasUser && config && !config._retried) {
      config._retried = true;
      try {
        await refreshTokens();
        return axios(config);
      } catch (refreshErr) {
        localStorage.removeItem(AUTH_USER_KEY);
        window.location.replace("/");
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  }
);

// Global registration
Chart.register(BoxPlotController, BoxAndWiskers);

class App extends Component {
  componentDidMount() {
    // Self-heal on boot: if HttpOnly cookies are valid, repopulate localStorage user info.
    // If status fails (401), try a silent refresh once before clearing.
    axios.get(`${API_BASE}/api/auth/status/`)
      .then((res) => {
        if (res.data?.isAuthenticated && res.data?.user) {
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));
        }
      })
      .catch(() => {
        refreshTokens()
          .then(() => axios.get(`${API_BASE}/api/auth/status/`))
          .then((res) => {
            if (res?.data?.isAuthenticated && res.data?.user) {
              localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));
            }
          })
          .catch(() => {
            localStorage.removeItem(AUTH_USER_KEY);
          });
      });

    this.refreshIntervalId = window.setInterval(() => {
      if (!localStorage.getItem(AUTH_USER_KEY)) return;
      refreshTokens().catch(() => {
        localStorage.removeItem(AUTH_USER_KEY);
      });
    }, REFRESH_INTERVAL_MS);

    // Refresh when the tab becomes visible again — covers laptop wake from
    // sleep and long-idle tabs, where the proactive interval may have missed
    // a tick.
    this.onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!localStorage.getItem(AUTH_USER_KEY)) return;
      refreshTokens().catch(() => {
        localStorage.removeItem(AUTH_USER_KEY);
        window.location.replace("/");
      });
    };
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  componentWillUnmount() {
    if (this.refreshIntervalId) window.clearInterval(this.refreshIntervalId);
    if (this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility);
  }

  render() {
    return (
      <div className="App">
        {/* Global toast container — render exactly once */}
         <ToastContainer
           position="top-right"
           autoClose={4000}
           newestOnTop
           closeOnClick
           pauseOnHover
           limit={4}          // cap concurrent toasts to avoid DOM thrash
           theme="light"
         />
        <BrowserRouter>
          {/* Sidebar renders only when logged in and drives the layout via
              --lssi-sidebar-width (see Sidebar.js); HeaderNav renders only
              the minimal login-page top bar when there's no session. */}
          <Sidebar />
          <Header />
          <div className="container" style={{ marginTop: "24px", marginBottom: "50px" }}>
            <Routes>
              {/* Public routes (login / home) */}
              <Route path="/" element={<Authorized /> } />
              <Route path="/auth" element={<Authorized />} />
              <Route path="/confirm-instructor-result" element={<ConfirmInstructorResult />} />
              <Route path="/confirm-utilidad-result" element={<ConfirmUtilidadResult />} />
              {/* Public legal pages (needed for Intuit review) */}
              <Route path="/legal/eula" element={<Eula />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />

              {/* Protected routes */}
              <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
              <Route path="/clientes" element={<RequireAuth><Clientes /></RequireAuth>} />
              <Route path="/clientes/importar" element={<RequireAuth><ImportarClientes /></RequireAuth>} />
              <Route path="/partners" element={<RequireAuth><Partners /></RequireAuth>} />
              <Route path="/productos" element={<RequireAuth><Productos /></RequireAuth>} />
              <Route path="/clasificacion-productos" element={<RequireAuth><ClasificacionProductos /></RequireAuth>} />
              <Route path="/costos" element={<RequireAuth><Costo /></RequireAuth>} />
              <Route path="/proyectos" element={<RequireAuth><Proyectos /></RequireAuth>} />
              <Route path="/sedes" element={<RequireAuth><Sede /></RequireAuth>} />
              <Route path="/multi_com" element={<RequireAuth><MultiCom /></RequireAuth>} />
              <Route path="/utilidad" element={<RequireAuth><Utilidades /></RequireAuth>} />
              <Route path="/detalle_proyectos" element={<RequireAuth><DetalleProyectos /></RequireAuth>} />
              <Route path="/detalle-proyectos/:proyectoCode" element={<RequireAuth><DetalleProyectos /></RequireAuth>} />
              <Route path="/proyectos_batch" element={<RequireAuth><Proyecto_batch /></RequireAuth>} />
              <Route path="/subproyectos_batch" element={<RequireAuth><Subproyecto_batch /></RequireAuth>} />
              <Route path="/movimientos" element={<RequireAuth><Movimiento /></RequireAuth>} />
              <Route path="/estado_instructor" element={<RequireAuth><EstadoInstructor /></RequireAuth>} />
              <Route path="/BoxScore" element={<RequireAuth><BoxScore /></RequireAuth>} />
              <Route path="/box-score-historico" element={<RequireAuth><BoxScoreHistorical /></RequireAuth>} />
              <Route path="/week" element={<RequireAuth><WeekChart /></RequireAuth>} />
              <Route path="/spc" element={<RequireAuth><SpcComponent /></RequireAuth>} />
              <Route path="/proyecto_con_causa" element={<RequireAuth><Pcc /></RequireAuth>} />
              <Route path="/lideres_proyectos" element={<RequireAuth><Lideres /></RequireAuth>} />
              <Route path="/project/:projectNumber" element={<RequireAuth><Project /></RequireAuth>} />
              <Route path="/proveedores" element={<RequireAuth><Vendors /></RequireAuth>} />
              <Route path="/create-invoice" element={<RequireAuth><InvoiceForm /></RequireAuth>} />
              <Route path="/analytics" element={<RequireAuth><Analytics /></RequireAuth>} />
              <Route path="/reportes" element={<RequireAuth><Reportes /></RequireAuth>} />
              <Route path="/dashboards" element={<RequireAuth><Dashboards /></RequireAuth>} />
              <Route path="/business-intelligence" element={<RequireAuth><BusinessIntelligence /></RequireAuth>} />
              <Route path="/bi-online" element={<RequireAuth><BiOnline /></RequireAuth>} />
              <Route path="/security-settings" element={<RequireAuth><SecuritySettings /></RequireAuth>} />
              <Route path="/user-admin" element={<RequireAuth><UserAdmin /></RequireAuth>} />
              <Route path="/ai" element={<RequireAuth><AI /></RequireAuth>} />
              <Route path="/ai-admin" element={<RequireAuth><AIAdmin /></RequireAuth>} />
              <Route path="/estado" element={<RequireAuth><Estado /></RequireAuth>} />

              {/* Fallback */}
              <Route path="*" element={<Home />} />
            </Routes>
          </div>
        </BrowserRouter>
      </div>
    );
  }
}

export default App;